import { useCallback } from 'react';
import {
  useTabsStore,
  setRequestTabs,
  setCollectionTabs,
  setSchemaTabs,
  setDbManagerTabs,
  setTaskManagerTabs,
  setProjectsTabs,
  setDocumentTabs,
  setTabStripOrder,
  setActiveRequestTabId,
  setActiveCollectionTabId,
  setActiveSchemaTabId,
  setActiveDbManagerTabId,
  setActiveTaskManagerTabId,
  setActiveProjectsTabId,
  setActiveDocumentTabId,
  setActiveTabType,
  guardDirtyDbTab,
  duplicateDbManagerTab,
} from '../../store/tabsStore';
import { documentCloseFallback, tabsOfWorkspace } from '../../store/documentTabWorkspaces';
import { useCollectionsStore, setSelectedCollection, setSelectedFolder } from '../../store/collectionsStore';
import { useWorkspaceStore } from '../../store/workspaceStore';
import { defaultRequestAuth } from '../../utils/requestAuth';
import { removeTabStripEntry, insertTabStripEntryAfter } from '../../utils/workspaceTabPersistence';
import { removeWorkspaceTabResponses, clearWorkspaceTabResponses } from '../../services/workspaceTabStore';
import { isWebApp } from '../../platform/target';
import { newTabId, EXTENSION_STRIP_TAB_TYPES } from './tabIds';

/** Closing a tab, duplicating it, and closing others / all. */
export function useTabClosing({
  createNewRequestTab,
}) {
  const closeTab = useCallback((tabId, tabType = 'request') => {
    // Closing the DB tab you are looking at throws its staged edits away just
    // as surely as switching off it does.
    if (
      tabType === 'db-manager-table' &&
      tabId === useTabsStore.getState().activeDbManagerTabId &&
      guardDirtyDbTab(() => closeTab(tabId, tabType))
    ) {
      return;
    }
    setTabStripOrder((prev) => removeTabStripEntry(prev, tabType, tabId));

    const {
      requestTabs, collectionTabs, schemaTabs, dbManagerTabs, taskManagerTabs, documentTabs, projectsTabs,
      activeRequestTabId, activeCollectionTabId, activeSchemaTabId, activeDbManagerTabId, activeTaskManagerTabId, activeDocumentTabId, activeProjectsTabId,
    } = useTabsStore.getState();
    const collections = useCollectionsStore.getState().collections;
    const currentWorkspace = useWorkspaceStore.getState().currentWorkspace;

    if (tabType === 'request') {
      const tabIndex = requestTabs.findIndex(tab => tab.id === tabId);
      const newTabs = requestTabs.filter(tab => tab.id !== tabId);

      if (currentWorkspace?.id) {
        removeWorkspaceTabResponses(currentWorkspace.id, [tabId]).catch(() => {});
      }

      if (newTabs.length === 0) {
        // Closing the only request tab: drop it first so it doesn't linger
        // (createNewRequestTab appends to the current requestTabs), then open a
        // fresh clean default tab so there's always exactly one request tab.
        setRequestTabs([]);
        createNewRequestTab();
        return;
      }

      setRequestTabs(newTabs);

      // If we closed the active tab, switch to adjacent tab
      if (tabId === activeRequestTabId) {
        const newActiveIndex = Math.min(tabIndex, newTabs.length - 1);
        setActiveRequestTabId(newTabs[newActiveIndex].id);
        setActiveTabType('request');
      }
    } else if (tabType === 'collection') {
      const newTabs = collectionTabs.filter((tab) => tab.id !== tabId);
      setCollectionTabs(newTabs);

      if (tabId === activeCollectionTabId) {
        if (newTabs.length > 0) {
          setActiveCollectionTabId(newTabs[newTabs.length - 1].id);
          setActiveTabType('collection');
          const last = newTabs[newTabs.length - 1];
          const col = collections.find((c) => String(c.id) === String(last.collectionId));
          setSelectedCollection(col || null);
        } else {
          setActiveCollectionTabId(null);
          setActiveTabType('request');
          setSelectedCollection(null);
        }
      }
    } else if (tabType === 'schema') {
      const newTabs = schemaTabs.filter((tab) => tab.id !== tabId);
      setSchemaTabs(newTabs);
      if (tabId === activeSchemaTabId) {
        if (newTabs.length > 0) {
          setActiveSchemaTabId(newTabs[newTabs.length - 1].id);
          setActiveTabType('schema');
        } else {
          setActiveSchemaTabId(null);
          setActiveTabType('request');
        }
      }
    } else if (tabType === 'db-manager-table') {
      const newTabs = dbManagerTabs.filter((tab) => tab.id !== tabId);
      setDbManagerTabs(newTabs);
      if (tabId === activeDbManagerTabId) {
        if (newTabs.length > 0) {
          setActiveDbManagerTabId(newTabs[newTabs.length - 1].id);
        } else {
          setActiveDbManagerTabId(null);
        }
        // DB Manager's tab strip is isolated from Collections — stay on
        // 'db-manager-table' (showing its own empty state if none are left)
        // rather than falling through to the request-tab context.
        setActiveTabType('db-manager-table');
      }
    } else if (tabType === 'task-manager-board') {
      const closingTab = taskManagerTabs.find((t) => t.id === tabId);
      if (closingTab?.pinned) return; // the pinned "Boards" tab is never closeable via the UI
      const newTabs = taskManagerTabs.filter((t) => t.id !== tabId);
      setTaskManagerTabs(newTabs);
      if (tabId === activeTaskManagerTabId) {
        // Prefer another open board tab; fall back to the pinned "Boards"
        // tab (which always exists) rather than leaving nothing active.
        const fallback = [...newTabs].reverse().find((t) => !t.pinned) || newTabs.find((t) => t.pinned);
        setActiveTaskManagerTabId(fallback ? fallback.id : null);
        setActiveTabType('task-manager-board');
      }
    } else if (tabType === 'projects-project') {
      // Same rules as board tabs: the pinned "Projects" tab never closes.
      const closingTab = projectsTabs.find((t) => t.id === tabId);
      if (closingTab?.pinned) return;
      const newTabs = projectsTabs.filter((t) => t.id !== tabId);
      setProjectsTabs(newTabs);
      if (tabId === activeProjectsTabId) {
        const fallback = [...newTabs].reverse().find((t) => !t.pinned) || newTabs.find((t) => t.pinned);
        setActiveProjectsTabId(fallback ? fallback.id : null);
        setActiveTabType('projects-project');
      }
    } else if (tabType === 'document-editor') {
      const newTabs = documentTabs.filter((t) => t.id !== tabId);
      setDocumentTabs(newTabs);
      if (tabId === activeDocumentTabId) {
        // Documents' tab strip is isolated from Collections — stay on
        // 'document-editor' (showing its own empty state if none are left)
        // rather than falling through to the request-tab context.
        setActiveDocumentTabId(documentCloseFallback(newTabs));
        setActiveTabType('document-editor');
      }
    }
  }, [createNewRequestTab]);

  const duplicateTab = useCallback((tabId) => {
    // DB Manager tabs duplicate too, and their copy rules are their own —
    // which view state carries over, which staged work must not. The store
    // owns that; this just routes.
    if (useTabsStore.getState().dbManagerTabs.some((t) => t.id === tabId)) {
      duplicateDbManagerTab(tabId);
      return;
    }
    const tab = useTabsStore.getState().requestTabs.find((t) => t.id === tabId);
    if (!tab) return;
    const id = newTabId();
    const newTab = {
      id,
      name: tab.name,
      method: tab.method,
      url: tab.url,
      headers: tab.headers ? [...tab.headers] : [],
      params: tab.params ? [...tab.params] : [],
      cookies: tab.cookies ? [...tab.cookies] : [],
      body: tab.body,
      bodyType: tab.bodyType,
      rawBodyLanguage: tab.rawBodyLanguage,
      formDataFields: tab.formDataFields ? [...tab.formDataFields] : undefined,
      auth: tab.auth,
      preRequestScript: tab.preRequestScript ?? '',
      testScript: tab.testScript ?? '',
      mock: tab.mock,
      hasUnsavedChanges: true,
      response: null,
      loading: false,
      collectionId: tab.collectionId || null,
      folderId: tab.folderId || null,
      originalId: null,
      isNewRequest: true,
      editorTab: tab.editorTab ?? 'params',
      responseTab: tab.responseTab ?? 'body',
      type: tab.type || 'http',
    };
    // Insert immediately after the source tab, not at the end
    setRequestTabs(prev => {
      const idx = prev.findIndex(t => t.id === tabId);
      const next = [...prev];
      next.splice(idx === -1 ? next.length : idx + 1, 0, newTab);
      return next;
    });
    setTabStripOrder((prev) => insertTabStripEntryAfter(prev, 'request', tabId, { type: 'request', id }));
    setActiveRequestTabId(newTab.id);
    setActiveTabType('request');
  }, []);

  const closeOtherTabs = useCallback((tabId, tabType = 'request') => {
    // DB Manager's tab strip is isolated from Collections — "close other tabs"
    // only ever touches the strip the clicked tab belongs to.
    if (tabType === 'db-manager-table') {
      // The dirty tab is among the ones about to go unless it is the keeper.
      if (
        tabId !== useTabsStore.getState().activeDbManagerTabId &&
        guardDirtyDbTab(() => closeOtherTabs(tabId, tabType))
      ) {
        return;
      }
      const dbTabs = useTabsStore.getState().dbManagerTabs;
      const keep = dbTabs.find((t) => t.id === tabId);
      setDbManagerTabs(keep ? [keep] : []);
      setTabStripOrder((prev) => prev.filter((e) => e.type !== 'db-manager-table' || e.id === tabId));
      setActiveDbManagerTabId(keep ? keep.id : null);
      if (keep) setActiveTabType('db-manager-table');
      return;
    }
    if (tabType === 'task-manager-board') {
      // "Close other tabs" keeps the clicked tab and the pinned "Boards" tab
      // (which is always open, same as it's never individually closeable).
      const boardTabs = useTabsStore.getState().taskManagerTabs;
      const keep = boardTabs.find((t) => t.id === tabId);
      const pinned = boardTabs.find((t) => t.pinned);
      const survivors = [pinned, keep].filter(Boolean).filter((t, i, arr) => arr.findIndex((x) => x.id === t.id) === i);
      setTaskManagerTabs(survivors);
      setTabStripOrder((prev) => prev.filter((e) => e.type !== 'task-manager-board' || survivors.some((t) => t.id === e.id)));
      setActiveTaskManagerTabId(keep ? keep.id : pinned ? pinned.id : null);
      if (survivors.length > 0) setActiveTabType('task-manager-board');
      return;
    }
    if (tabType === 'projects-project') {
      const all = useTabsStore.getState().projectsTabs;
      const keep = all.find((t) => t.id === tabId);
      const pinned = all.find((t) => t.pinned);
      const survivors = [pinned, keep].filter(Boolean).filter((t, i, arr) => arr.findIndex((x) => x.id === t.id) === i);
      setProjectsTabs(survivors);
      setTabStripOrder((prev) => prev.filter((e) => e.type !== 'projects-project' || survivors.some((t) => t.id === e.id)));
      setActiveProjectsTabId(keep ? keep.id : pinned ? pinned.id : null);
      if (survivors.length > 0) setActiveTabType('projects-project');
      return;
    }
    if (tabType === 'document-editor') {
      // Only the strip in view: other workspaces' document tabs stay open.
      const docTabs = useTabsStore.getState().documentTabs;
      const keep = docTabs.find((t) => t.id === tabId);
      const gone = new Set(tabsOfWorkspace(docTabs).filter((t) => t.id !== tabId).map((t) => t.id));
      setDocumentTabs(docTabs.filter((t) => !gone.has(t.id)));
      setTabStripOrder((prev) => prev.filter((e) => e.type !== 'document-editor' || !gone.has(e.id)));
      setActiveDocumentTabId(keep ? keep.id : null);
      if (keep) setActiveTabType('document-editor');
      return;
    }

    const requestTabs = useTabsStore.getState().requestTabs;
    const currentWorkspace = useWorkspaceStore.getState().currentWorkspace;
    const keep = requestTabs.find((t) => t.id === tabId);
    const others = requestTabs.filter((t) => t.id !== tabId).map((t) => t.id);
    if (currentWorkspace?.id && others.length > 0) {
      removeWorkspaceTabResponses(currentWorkspace.id, others).catch(() => {});
    }
    setRequestTabs(keep ? [keep] : []);
    setTabStripOrder((prev) => {
      const preserved = prev.filter((e) => e.type === 'db-manager-table' || e.type === 'task-manager-board' || e.type === 'projects-project' || e.type === 'document-editor');
      return keep ? [{ type: 'request', id: keep.id }, ...preserved] : preserved;
    });
    if (keep) {
      setActiveRequestTabId(keep.id);
      setActiveTabType('request');
    }
    setCollectionTabs([]);
    setSchemaTabs([]);
    setActiveCollectionTabId(null);
    setActiveSchemaTabId(null);
    setSelectedCollection(null);
    setSelectedFolder(null);
  }, []);

  const closeAllTabs = useCallback(({ clearPersistedResponses = true, scope = 'collections' } = {}) => {
    // DB Manager's tab strip is isolated from Collections — "close all tabs"
    // only clears the strip that's currently in view.
    if (scope === 'db-manager') {
      if (guardDirtyDbTab(() => closeAllTabs({ clearPersistedResponses, scope }))) return;
      setDbManagerTabs([]);
      setTabStripOrder((prev) => prev.filter((e) => e.type !== 'db-manager-table'));
      setActiveDbManagerTabId(null);
      setActiveTabType('db-manager-table');
      return;
    }
    if (scope === 'task-manager') {
      // "Close all" keeps the pinned "Boards" tab — it's always open.
      const boardTabs = useTabsStore.getState().taskManagerTabs;
      const pinned = boardTabs.find((t) => t.pinned);
      const survivors = pinned ? [pinned] : [];
      setTaskManagerTabs(survivors);
      setTabStripOrder((prev) => prev.filter((e) => e.type !== 'task-manager-board' || survivors.some((t) => t.id === e.id)));
      setActiveTaskManagerTabId(pinned ? pinned.id : null);
      setActiveTabType('task-manager-board');
      return;
    }
    if (scope === 'projects') {
      const pinned = useTabsStore.getState().projectsTabs.find((t) => t.pinned);
      const survivors = pinned ? [pinned] : [];
      setProjectsTabs(survivors);
      setTabStripOrder((prev) => prev.filter((e) => e.type !== 'projects-project' || survivors.some((t) => t.id === e.id)));
      setActiveProjectsTabId(pinned ? pinned.id : null);
      setActiveTabType('projects-project');
      return;
    }
    if (scope === 'documents') {
      // Documents' tab strip is isolated from Collections — "close all tabs"
      // only clears the document strip that's currently in view.
      // …of the workspace in view: the other workspaces keep their tabs.
      const gone = new Set(tabsOfWorkspace(useTabsStore.getState().documentTabs).map((t) => t.id));
      setDocumentTabs((prev) => prev.filter((t) => !gone.has(t.id)));
      setTabStripOrder((prev) => prev.filter((e) => e.type !== 'document-editor' || !gone.has(e.id)));
      setActiveDocumentTabId(null);
      setActiveTabType('document-editor');
      return;
    }

    const currentWorkspace = useWorkspaceStore.getState().currentWorkspace;
    if (clearPersistedResponses && currentWorkspace?.id) {
      clearWorkspaceTabResponses(currentWorkspace.id).catch(() => {});
    }
    const id = newTabId();
    const freshTab = {
      id,
      name: null,
      method: 'GET',
      url: '',
      headers: [{ key: '', value: '', enabled: true }],
      params: [{ key: '', value: '', enabled: true }],
      cookies: [{ key: '', value: '', enabled: true }],
      body: '',
      bodyType: 'none',
      auth: defaultRequestAuth(),
      hasUnsavedChanges: false,
      response: null,
      loading: false,
      collectionId: null,
      originalId: null,
      isNewRequest: true,
      type: 'http',
    };
    setCollectionTabs([]);
    setSchemaTabs([]);
    setRequestTabs([freshTab]);
    setTabStripOrder((prev) => [
      { type: 'request', id },
      ...prev.filter((e) => EXTENSION_STRIP_TAB_TYPES.has(e.type)),
    ]);
    setActiveRequestTabId(id);
    setActiveCollectionTabId(null);
    setActiveSchemaTabId(null);
    // Web app only: it opens on Boards and resets tabs when no workspace loads,
    // so keep the extension page's tab type or the request builder paints into
    // Boards. The desktop keeps passing through 'request' — App.js records that
    // as the Collections strip's type to return to, and its tabs were just
    // cleared (review R2).
    setActiveTabType((prev) => (isWebApp() && EXTENSION_STRIP_TAB_TYPES.has(prev) ? prev : 'request'));
    setSelectedCollection(null);
  }, []);

  return { closeTab, duplicateTab, closeOtherTabs, closeAllTabs };
}
